import React, { useState, useMemo } from "react";
import { X, Calculator, AlertTriangle, CheckCircle2, Wand2, Info, BookOpen, Book } from "lucide-react";
import { TRIM_SIZES, PAPER_TYPES } from "./BookSetupModal";

const HARDCOVER_TRIMS = new Set(["5.5x8.5", "6x9", "6.14x9.21", "7x10", "8.25x11"]);
const HARDCOVER_BOUNDS = [75, 550];
const HARDCOVER_SPINE_WRAP = 0.06;

/**
 * Resize-for-KDP modal.
 *
 * Lets the user fix a cover that was rejected by KDP for wrong dimensions
 * — typically because the interior PDF ended up with a different page
 * count than the cover was designed for, which means the spine width
 * (and therefore the total wrap width) no longer matches what KDP
 * computes from the interior PDF.
 *
 * The modal pre-fills with the current setup and exposes a "match KDP's
 * expected dimensions" helper: paste the expected total width that KDP
 * reported in the rejection message, and we'll reverse-engineer the
 * correct page count for the current trim+paper combo.
 *
 * Front and back cover canvas content is preserved exactly when spine
 * width changes (only the spine panel's width changes). If the trim
 * size changes the height/width of front/back also change, so we warn
 * the user that elements may need repositioning.
 */
const BLEED_IN_PB = 0.125;
const BLEED_IN_HC = 0.51;

const ResizeKDPModal = ({ open, onClose, current, onConfirm }) => {
  const [form, setForm] = useState({
    trim_size: current?.trim_size || "6x9",
    page_count: current?.page_count || 200,
    paper_type: current?.paper_type || "white",
    binding_type: current?.binding_type || "paperback",
  });
  const [kdpExpected, setKdpExpected] = useState("");

  const update = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "binding_type" && v === "hardcover" && !HARDCOVER_TRIMS.has(next.trim_size)) {
      next.trim_size = "6x9";
    }
    return next;
  });

  const isHardcover = form.binding_type === "hardcover";
  const BLEED_IN = isHardcover ? BLEED_IN_HC : BLEED_IN_PB;
  const currentBleed = (current?.binding_type === "hardcover") ? BLEED_IN_HC : BLEED_IN_PB;

  const trim = useMemo(
    () => TRIM_SIZES.find((t) => t.value === form.trim_size) || TRIM_SIZES[0],
    [form.trim_size]
  );
  const paper = useMemo(
    () => PAPER_TYPES.find((p) => p.value === form.paper_type) || PAPER_TYPES[0],
    [form.paper_type]
  );
  const availableTrims = useMemo(() =>
    isHardcover ? TRIM_SIZES.filter((t) => HARDCOVER_TRIMS.has(t.value)) : TRIM_SIZES,
    [isHardcover]
  );
  const pages = Math.max(1, parseInt(form.page_count) || 0);

  const spineWidth = useMemo(() => {
    if (!paper || !pages) return 0;
    const base = pages * paper.multiplier;
    const extra = isHardcover ? HARDCOVER_SPINE_WRAP : 0;
    return Math.round((base + extra) * 10000) / 10000;
  }, [paper, pages, isHardcover]);

  const totalWrap = useMemo(() => ({
    width: trim.w * 2 + spineWidth + BLEED_IN * 2,
    height: trim.h + BLEED_IN * 2,
  }), [trim, spineWidth, BLEED_IN]);

  const currentTotal = useMemo(() => {
    const cTrim = TRIM_SIZES.find((t) => t.value === current?.trim_size) || trim;
    return {
      width: cTrim.w * 2 + (current?.spine_width_inches || 0) + currentBleed * 2,
      height: cTrim.h + currentBleed * 2,
    };
  }, [current, trim, currentBleed]);

  // Try to parse "11.106x8.250" / "11.106 × 8.250" / "11.106, 8.25"
  const parsedExpected = useMemo(() => {
    if (!kdpExpected) return null;
    const m = kdpExpected.match(/([0-9]+\.?[0-9]*)\s*[x×, ]+\s*([0-9]+\.?[0-9]*)/i);
    if (!m) return null;
    const w = parseFloat(m[1]);
    const h = parseFloat(m[2]);
    if (!w || !h) return null;
    return { width: w, height: h };
  }, [kdpExpected]);

  // Reverse the KDP-expected width into a suggested page count.
  // expected_w = trim.w * 2 + spine + 2*bleed
  // spine = expected_w - trim.w*2 - 2*bleed
  // pages = round((spine - hardcover_wrap) / multiplier)
  const suggestion = useMemo(() => {
    if (!parsedExpected) return null;
    // First, see if the height matches a trim size at all
    const matchingTrim = TRIM_SIZES.find(
      (t) => Math.abs(t.h + BLEED_IN * 2 - parsedExpected.height) < 0.02
    );
    const useTrim = matchingTrim || trim;
    const requiredSpine = parsedExpected.width - useTrim.w * 2 - BLEED_IN * 2;
    if (requiredSpine < 0) return { error: "Expected width is smaller than two trim widths + bleed — check the binding type and trim size." };
    const paperContribution = requiredSpine - (isHardcover ? HARDCOVER_SPINE_WRAP : 0);
    const suggestedPages = Math.max(1, Math.round(paperContribution / paper.multiplier));
    return {
      trim_size: useTrim.value,
      trim_label: useTrim.label,
      spine: Math.round(requiredSpine * 10000) / 10000,
      pages: suggestedPages,
      paper_label: paper.label,
      heightMismatch: !matchingTrim,
    };
  }, [parsedExpected, trim, paper, BLEED_IN, isHardcover]);

  const applySuggestion = () => {
    if (!suggestion || suggestion.error) return;
    setForm((f) => ({
      ...f,
      trim_size: suggestion.trim_size,
      page_count: suggestion.pages,
    }));
  };

  const trimChanged = form.trim_size !== current?.trim_size;
  const bindingChanged = form.binding_type !== (current?.binding_type || "paperback");
  const spineChanged = Math.abs(spineWidth - (current?.spine_width_inches || 0)) > 0.0001;
  const widthDelta = totalWrap.width - currentTotal.width;
  const heightDelta = totalWrap.height - currentTotal.height;

  // KDP page-count bounds check
  const bounds = isHardcover
    ? HARDCOVER_BOUNDS
    : (trim.bounds?.[paper.value] || null);
  const pageError = useMemo(() => {
    if (!pages) return null;
    if (!bounds) return `${paper.label} is not available for this trim size on KDP.`;
    const [min, max] = bounds;
    if (pages < min) return `KDP ${isHardcover ? "hardcover" : "paperback"} requires at least ${min} pages.`;
    if (pages > max) return `KDP ${isHardcover ? "hardcover" : "paperback"} allows up to ${max} pages.`;
    return null;
  }, [pages, bounds, paper.label, isHardcover]);

  const isValid = pages > 0 && !pageError && (spineChanged || trimChanged || bindingChanged || form.paper_type !== current?.paper_type);

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm({
      trim_size: form.trim_size,
      page_count: pages,
      paper_type: form.paper_type,
      binding_type: form.binding_type,
      spine_width_inches: spineWidth,
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2C2A]/50 backdrop-blur-sm px-4">
      <div
        className="w-full max-w-2xl bg-white rounded-[12px] border border-[#D3D1C7] shadow-2xl max-h-[92vh] flex flex-col"
        data-testid="resize-kdp-modal"
      >
        <div className="px-7 py-5 border-b border-[#D3D1C7] flex items-start justify-between">
          <div>
            <h2
              className="text-2xl tracking-tight"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
            >
              Resize for KDP
            </h2>
            <p className="text-sm text-[#5F5E5A] mt-0.5">
              Adjust trim, page count, or paper so your cover dimensions match what KDP expects.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]"
            aria-label="Close"
            data-testid="resize-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-7 space-y-5 overflow-y-auto">
          {/* KDP error helper */}
          <div className="border border-[#534AB7]/30 bg-[#534AB7]/[0.04] rounded-[8px] p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-[#534AB7]" />
              <span className="text-xs uppercase tracking-[0.18em] text-[#534AB7] font-medium">
                Paste KDP's expected dimensions
              </span>
            </div>
            <p className="text-[12px] text-[#5F5E5A] leading-snug">
              Copy the "expected cover size" from KDP's rejection message
              (e.g. <span className="font-mono">11.106 × 8.250</span>) and we'll
              work out exactly which page count gets you there.
            </p>
            <div className="flex gap-2">
              <input
                value={kdpExpected}
                onChange={(e) => setKdpExpected(e.target.value)}
                placeholder="11.106 x 8.250"
                data-testid="resize-kdp-expected"
                className="flex-1 px-3 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-sm font-mono focus:outline-none focus:border-[#534AB7]"
              />
              <button
                onClick={applySuggestion}
                disabled={!suggestion || suggestion.error}
                data-testid="resize-apply-suggestion"
                className="px-4 py-2 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50"
              >
                Apply
              </button>
            </div>
            {suggestion && !suggestion.error && (
              <div className="text-[12px] text-[#2C2C2A] bg-white border border-[#D3D1C7] rounded-md px-3 py-2" data-testid="resize-suggestion">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1D9E75] mt-0.5 shrink-0" />
                  <div>
                    Set trim to <strong>{suggestion.trim_label}</strong>,{" "}
                    page count to <strong>{suggestion.pages}</strong> pages,{" "}
                    paper to <strong>{suggestion.paper_label}</strong> →{" "}
                    spine becomes <strong>{suggestion.spine.toFixed(4)}"</strong>.
                    {suggestion.heightMismatch && (
                      <div className="mt-1 text-[#BA7517]">
                        <AlertTriangle className="inline w-3 h-3 mr-0.5" />
                        Height doesn't match any KDP trim — double-check your trim size.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {suggestion?.error && (
              <div className="text-[12px] text-[#BA7517]">{suggestion.error}</div>
            )}
          </div>

          {/* Editable fields */}
          <div className="grid grid-cols-1 gap-4">
            <Field label="Binding type">
              <div className="grid grid-cols-2 gap-2" data-testid="resize-binding-toggle">
                <button
                  type="button"
                  onClick={() => update("binding_type", "paperback")}
                  data-testid="resize-binding-paperback"
                  className={`px-3 py-2 rounded-[8px] text-sm font-medium border flex items-center justify-center gap-2 transition-colors ${
                    !isHardcover
                      ? "bg-[#534AB7] text-white border-[#534AB7]"
                      : "bg-white text-[#2C2C2A] border-[#D3D1C7] hover:bg-[#F1EFE8]"
                  }`}
                >
                  <BookOpen className="w-4 h-4" /> Paperback
                </button>
                <button
                  type="button"
                  onClick={() => update("binding_type", "hardcover")}
                  data-testid="resize-binding-hardcover"
                  className={`px-3 py-2 rounded-[8px] text-sm font-medium border flex items-center justify-center gap-2 transition-colors ${
                    isHardcover
                      ? "bg-[#534AB7] text-white border-[#534AB7]"
                      : "bg-white text-[#2C2C2A] border-[#D3D1C7] hover:bg-[#F1EFE8]"
                  }`}
                >
                  <Book className="w-4 h-4" /> Hardcover
                </button>
              </div>
            </Field>

            <Field label="Trim size">
              <select
                value={form.trim_size}
                onChange={(e) => update("trim_size", e.target.value)}
                data-testid="resize-trim-select"
                className={inputCls}
              >
                {availableTrims.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Page count">
                <input
                  type="number"
                  min={1}
                  max={900}
                  value={form.page_count}
                  onChange={(e) => update("page_count", e.target.value)}
                  data-testid="resize-pages-input"
                  className={inputCls}
                />
                {bounds && (
                  <div className="mt-1 text-[10px] text-[#5F5E5A]">
                    KDP allows {bounds[0]}–{bounds[1]} pages for this combo.
                  </div>
                )}
              </Field>
              <Field label="Interior paper">
                <select
                  value={form.paper_type}
                  onChange={(e) => update("paper_type", e.target.value)}
                  data-testid="resize-paper-select"
                  className={inputCls}
                >
                  {PAPER_TYPES.map((p) => {
                    const available = !!trim.bounds?.[p.value];
                    return (
                      <option key={p.value} value={p.value} disabled={!available}>
                        {p.label}{available ? "" : " (not available)"}
                      </option>
                    );
                  })}
                </select>
              </Field>
            </div>
          </div>

          {pageError && (
            <div className="bg-[#FFF4E5] border border-[#E4D6BA] rounded-[8px] p-3 flex items-start gap-2 text-sm text-[#7A5C0E]">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-[#BA7517]" />
              <div>{pageError}</div>
            </div>
          )}

          {/* Dimension comparison */}
          <div className="grid grid-cols-2 gap-3" data-testid="resize-comparison">
            <Box label="Current">
              <Stat label="Spine" value={`${(current?.spine_width_inches || 0).toFixed(4)}"`} />
              <Stat label="Total width" value={`${currentTotal.width.toFixed(3)}"`} />
              <Stat label="Total height" value={`${currentTotal.height.toFixed(3)}"`} />
            </Box>
            <Box label="After resize" highlight={isValid}>
              <Stat
                label="Spine"
                value={`${spineWidth.toFixed(4)}"`}
                delta={spineChanged ? spineWidth - (current?.spine_width_inches || 0) : null}
              />
              <Stat
                label="Total width"
                value={`${totalWrap.width.toFixed(3)}"`}
                delta={widthDelta}
              />
              <Stat
                label="Total height"
                value={`${totalWrap.height.toFixed(3)}"`}
                delta={heightDelta}
              />
            </Box>
          </div>

          {trimChanged && (
            <div className="bg-[#FFF4E5] border border-[#E4D6BA] rounded-[8px] p-3 flex items-start gap-2 text-xs text-[#7A5C0E]">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#BA7517]" />
              <div>
                You're changing trim size — front & back panels will resize. Existing text or
                images may need to be repositioned. Spine-only changes preserve your front and
                back layouts unchanged.
              </div>
            </div>
          )}

          <div className="bg-[#F1EFE8]/70 border border-[#D3D1C7] rounded-[8px] p-4 flex items-start gap-3">
            <Calculator className="w-5 h-5 text-[#534AB7] mt-0.5" strokeWidth={1.5} />
            <div className="text-[12px] text-[#5F5E5A] leading-snug">
              KDP computes the spine width from your interior PDF's page count and paper
              type using the formula <span className="font-mono text-[#2C2C2A]">pages × paper_multiplier</span>.
              If your interior PDF page count changed after you designed the cover, the
              spine — and therefore the total wrap width — no longer matches. Adjust the
              page count above to match your <em>final</em> interior PDF, then re-export.
            </div>
          </div>
        </div>

        <div className="px-7 py-4 border-t border-[#D3D1C7] flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            data-testid="resize-cancel-btn"
            className="px-5 py-2.5 rounded-[8px] border border-[#D3D1C7] text-sm hover:bg-[#F1EFE8]"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            data-testid="resize-confirm-btn"
            className="px-6 py-2.5 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50"
          >
            Apply new size
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-[#5F5E5A] uppercase tracking-wider mb-1.5">{label}</span>
    {children}
  </label>
);

const Box = ({ label, highlight, children }) => (
  <div className={`rounded-[8px] border p-3 ${highlight ? "border-[#534AB7] bg-[#534AB7]/[0.04]" : "border-[#D3D1C7] bg-white"}`}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5F5E5A] mb-2">{label}</div>
    <div className="space-y-1">{children}</div>
  </div>
);

const Stat = ({ label, value, delta }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-[#5F5E5A] text-[12px]">{label}</span>
    <span className="font-mono text-[13px] text-[#2C2C2A] flex items-center gap-1.5">
      {value}
      {delta != null && Math.abs(delta) > 0.0005 && (
        <span className={delta > 0 ? "text-[#1D9E75]" : "text-[#D74343]"}>
          {delta > 0 ? "+" : ""}{delta.toFixed(3)}"
        </span>
      )}
    </span>
  </div>
);

const inputCls = "w-full px-3 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-sm text-[#2C2C2A] focus:outline-none focus:border-[#534AB7] focus:ring-1 focus:ring-[#534AB7]";

export default ResizeKDPModal;
